import DeleteOutlined from "@ant-design/icons/lib/icons/DeleteOutlined";
import DislikeOutlined from "@ant-design/icons/lib/icons/DislikeOutlined";
import NotificationOutlined from "@ant-design/icons/lib/icons/NotificationOutlined";
import RedoOutlined from "@ant-design/icons/lib/icons/RedoOutlined";
import { useReportFormSubmit } from "@app/_shared_components/useReportFormSubmit";
import { useResponsiveModalWidth } from "@app/_shared_components/useResponsiveModalWidth";
import { useReportMutations } from "@app/entities/report/mutations";
import { T_Report, T_ReportStatus } from "@app/entities/report/types";
import { RowActionItem } from "@app/pages/admin/_components/RowActionsMenu";
import { useReport } from "@app/providers/ReportContextProvider";
import { SWARM_LOCATIONS, SWARM_SIZES } from "@root/constants";
import Modal from "antd/lib/modal";
import React, { useEffect, useState } from "react";

type Args = {
  report: T_Report | null;
  onClose: () => void;
};

export type MapReportTabKey = "details" | "comments" | "alerts" | "history";

export type DescriptionItem = {
  key: string;
  label: string;
  children: React.ReactNode;
};

export type TabConfig = { key: MapReportTabKey; label: string };

const STATUS_LABEL: Record<T_ReportStatus, string> = {
  a: "Active",
  c: "Claimed",
  b: "Bad",
};

const STATUS_COLOR: Record<T_ReportStatus, string> = {
  a: "gold",
  c: "green",
  b: "red",
};

const sizeOptions = SWARM_SIZES.map((size) => ({ value: size, label: size }));
const swarmLocationOptions = SWARM_LOCATIONS.map((loc) => ({
  value: loc,
  label: loc,
}));

const BODY_MAX_HEIGHT = "calc(100vh - 180px)";

export const useMapReportModalLoader = ({ report, onClose }: Args) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<MapReportTabKey>("details");
  const [reportToMarkBad, setReportToMarkBad] = useState<T_Report | null>(null);
  const [badResetPromptOpen, setBadResetPromptOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  const { width, modalStyle } = useResponsiveModalWidth();
  // Anchor the modal near the top so the tabs stay at a fixed Y position when
  // switching between tabs of different heights (instead of vertically centering).
  // Mobile keeps top: 0 from useResponsiveModalWidth; desktop gets a small offset.
  const anchoredModalStyle = { ...modalStyle, top: modalStyle?.top ?? 20 };

  const { broadcastReport, resetReport, isMutationPending } =
    useReportMutations();
  const { deleteReport } = useReport();

  const {
    reportForm,
    previewUrl,
    file,
    handleFileChange,
    handleSubmit,
    isSubmitting,
  } = useReportFormSubmit({
    reportId: report?.reportId ?? -1,
    onSuccess: () => setIsEditing(false),
  });

  // Reset edit mode and tab when the underlying report identity changes.
  useEffect(() => {
    setIsEditing(false);
    setActiveTab("details");
    setReportToMarkBad(null);
    setBadResetPromptOpen(false);
    setImagePreviewOpen(false);
    if (report) {
      reportForm.resetFields();
    }
  }, [report?.reportId]);

  const startEditing = () => setIsEditing(true);
  const cancelEditing = () => {
    reportForm.resetFields();
    setIsEditing(false);
  };

  const openBadResetPrompt = () => setBadResetPromptOpen(true);
  const closeBadResetPrompt = () => setBadResetPromptOpen(false);

  const openImagePreview = () => setImagePreviewOpen(true);
  const closeImagePreview = () => setImagePreviewOpen(false);

  const handleClose = () => {
    if (isEditing && reportForm.isFieldsTouched()) {
      Modal.confirm({
        title: "Discard unsaved changes?",
        okText: "Discard",
        cancelText: "Keep editing",
        onOk: () => {
          cancelEditing();
          onClose();
        },
      });
      return;
    }
    onClose();
  };

  const confirmBadReset = (shouldAlert: boolean) => {
    if (!report) return;
    resetReport(
      { reportId: report.reportId, shouldAlert },
      {
        onSuccess: () => {
          setBadResetPromptOpen(false);
          onClose();
        },
      },
    );
  };

  const buildActionItems = (r: T_Report): RowActionItem[] => {
    const broadcastItem: RowActionItem = {
      key: "broadcast",
      label: "Broadcast",
      icon: React.createElement(NotificationOutlined),
      onClick: () =>
        broadcastReport(r.reportId, { onSuccess: () => onClose() }),
      confirm: {
        title: "Are you sure you want to broadcast this report?",
        description:
          "All members, regardless availability (with the exception of 'Do Not Disturb'), will be alerted. You will be charged for these alerts.",
      },
    };

    const activeResetItem: RowActionItem = {
      key: "reset",
      label: "Reset",
      icon: React.createElement(RedoOutlined),
      onClick: () =>
        resetReport({ reportId: r.reportId }, { onSuccess: () => onClose() }),
      confirm: {
        title: "Are you sure you want to reset this report?",
        description:
          "All available members will be alerted again. You will be charged for these alerts.",
      },
    };

    const badResetItem: RowActionItem = {
      key: "reset",
      label: "Reset",
      icon: React.createElement(RedoOutlined),
      onClick: () => openBadResetPrompt(),
    };

    const markBadItem: RowActionItem = {
      key: "markBad",
      label: "Mark as Bad",
      icon: React.createElement(DislikeOutlined),
      danger: true,
      onClick: () => setReportToMarkBad(r),
    };

    const deleteItem: RowActionItem = {
      key: "delete",
      label: "Delete",
      icon: React.createElement(DeleteOutlined),
      danger: true,
      onClick: () => {
        deleteReport(r.reportId);
        onClose();
      },
      confirm: {
        title: "Are you sure you want to delete this report?",
        description: "This action cannot be undone.",
      },
    };

    switch (r.status) {
      case "a":
        return [broadcastItem, activeResetItem, markBadItem, deleteItem];
      case "c":
        return r.belongsToCompany ? [markBadItem, deleteItem] : [];
      case "b":
        return [badResetItem, deleteItem];
      default:
        return [];
    }
  };

  const actionItems: RowActionItem[] = report ? buildActionItems(report) : [];

  const displayedImageUrl = previewUrl || report?.imageUrl || null;
  const showEditButtons = activeTab === "details";
  const statusLabel = report ? STATUS_LABEL[report.status] : "";
  const statusColor = report ? STATUS_COLOR[report.status] : "";

  const buildDescriptionItems = (
    imagePreview: React.ReactNode,
  ): DescriptionItem[] => {
    if (!report) return [];

    const items: DescriptionItem[] = [
      { key: "claimCode", label: "Claim Code", children: report.claimCode },
      {
        key: "dateReported",
        label: "Date Reported",
        children: new Date(report.dateCreated).toLocaleString(),
      },
    ];

    if (report.status === "c" && report.dateClaimed) {
      items.push({
        key: "dateClaimed",
        label: "Date Claimed",
        children: new Date(report.dateClaimed).toLocaleString(),
      });
    }

    if (report.status === "b" && report.dateMarkedBad) {
      items.push({
        key: "dateMarkedBad",
        label: "Date Marked Bad",
        children: new Date(report.dateMarkedBad).toLocaleString(),
      });
    }

    if (report.status === "b" && report.markedBadReason) {
      items.push({
        key: "markedBadReason",
        label: "Marked Bad Reason",
        children: report.markedBadReason,
      });
    }

    if (report.status === "b" && report.markedBadEntityName) {
      items.push({
        key: "markedBadBy",
        label: "Marked Bad By",
        children: report.markedBadEntityName,
      });
    }

    items.push(
      {
        key: "reporterName",
        label: "Reporter Name",
        children: report.reporterName,
      },
      {
        key: "reporterPhone",
        label: "Reporter Phone",
        children: report.reporterPhone || "—",
      },
      { key: "address", label: "Address", children: report.address },
    );

    if (!isEditing) {
      items.push(
        { key: "size", label: "Size", children: report.size || "—" },
        {
          key: "elevation",
          label: "Elevation",
          children: report.elevation ?? "—",
        },
        {
          key: "swarmLocation",
          label: "Swarm Location",
          children: report.swarmLocation || "—",
        },
        { key: "image", label: "Image", children: imagePreview ?? "—" },
        {
          key: "reporterNote",
          label: "Note",
          children: report.reporterNote || "—",
        },
      );
    }

    if (report.memberName) {
      items.push({
        key: "claimedBy",
        label: "Claimed By",
        children: report.memberName,
      });
    }

    if (report.status === "a" && report.previouslyClaimedBy) {
      items.push({
        key: "previouslyClaimedBy",
        label: "Previously Claimed By",
        children: report.previouslyClaimedBy,
      });
    }

    if (report.status === "a" && report.reasonUnClaimed) {
      items.push({
        key: "reasonUnClaimed",
        label: "Reason Un-Claimed",
        children: report.reasonUnClaimed,
      });
    }

    items.push(
      { key: "resetCount", label: "Reset Count", children: report.resetCount },
      {
        key: "broadcastCount",
        label: "Broadcast Count",
        children: report.broadcastCount,
      },
      { key: "alertCount", label: "Alert Count", children: report.alertCount },
    );

    return items;
  };

  const tabConfigs: TabConfig[] = [];
  if (report) {
    tabConfigs.push({ key: "details", label: "Details" });
    tabConfigs.push({
      key: "comments",
      label:
        report.commentCount > 0
          ? `Comments (${report.commentCount})`
          : "Comments",
    });
    if (report.alertCount > 0) {
      tabConfigs.push({
        key: "alerts",
        label: `Alerts (${report.alertCount})`,
      });
    }
    tabConfigs.push({ key: "history", label: "History" });
  }

  return {
    activeTab,
    setActiveTab,
    isEditing,
    startEditing,
    cancelEditing,
    actionItems,
    reportToMarkBad,
    setReportToMarkBad,
    badResetPromptOpen,
    openBadResetPrompt,
    closeBadResetPrompt,
    confirmBadReset,
    reportForm,
    previewUrl,
    file,
    handleFileChange,
    handleSubmit,
    isSubmitting,
    isMutationPending,
    width,
    anchoredModalStyle,
    bodyMaxHeight: BODY_MAX_HEIGHT,
    imagePreviewOpen,
    openImagePreview,
    closeImagePreview,
    displayedImageUrl,
    handleClose,
    showEditButtons,
    statusLabel,
    statusColor,
    sizeOptions,
    swarmLocationOptions,
    tabConfigs,
    buildDescriptionItems,
  };
};
